// Finance build — the Income Overview by-loan table. One row per loan with
// recognized income in the window; each loan number links to the per-loan
// income drill (carrying the same window params). Read-only.

import { Link } from "react-router-dom";
import {
  Card,
  EmptyState,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import type {
  IncomeOverviewLoanRowDto,
  IncomeOverviewParams,
} from "@/api/portfolioAdmin/incomeOverviewApi";
import { rangeSearch } from "./range";

interface IncomeLoanTableProps {
  rows: IncomeOverviewLoanRowDto[];
  params: IncomeOverviewParams;
}

export function IncomeLoanTable({ rows, params }: IncomeLoanTableProps) {
  const search = rangeSearch(params);
  return (
    <Card data-testid="income-overview-loans">
      <h2 className="m-0 mb-space-4 font-display text-h3 font-bold text-onyx">
        By loan
      </h2>
      {rows.length === 0 ? (
        <EmptyState
          title="No loan income in this window"
          description="No loans recognized income events in the selected range. Many loans only post income as their payments are serviced."
          variant="noResults"
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Loan</TH>
              <TH className="text-right">Retained</TH>
              <TH className="text-right">VFI</TH>
              <TH className="text-right">Outflow</TH>
              <TH className="text-right">Expected</TH>
              <TH className="text-right">Received</TH>
              <TH className="text-right">Variance</TH>
              <TH className="text-right">Events</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => (
              <TR key={row.loanId} data-testid={`income-overview-loan-row-${row.loanId}`}>
                <TD>
                  <Link
                    to={`/portfolio-admin/income-overview/loans/${row.loanId}${search}`}
                    className="text-gold-shadow hover:underline"
                    data-testid={`income-overview-loan-link-${row.loanId}`}
                  >
                    {row.loanNumber}
                  </Link>
                </TD>
                <TD className="text-right tabular-nums">
                  {formatCurrency(row.retainedReceived)}
                </TD>
                <TD className="text-right tabular-nums">
                  {formatCurrency(row.vfiReceived)}
                </TD>
                <TD className="text-right tabular-nums">
                  {formatCurrency(row.outflowReceived)}
                </TD>
                <TD className="text-right tabular-nums">
                  {formatCurrency(row.expected)}
                </TD>
                <TD className="text-right tabular-nums">
                  {formatCurrency(row.received)}
                </TD>
                <TD
                  className={
                    Math.abs(row.variance) >= 0.005
                      ? "text-right tabular-nums text-status-error"
                      : "text-right tabular-nums text-gray-mid"
                  }
                >
                  {formatCurrency(row.variance)}
                </TD>
                <TD className="text-right tabular-nums text-gray-mid">
                  {row.eventCount}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
